<template>
	<!-- Backdrop overlay -->
	<div
		v-if="expandSearch && !fullscreen"
		class="fixed inset-0 z-40"
		@click="handleOverlayClick"
	></div>
	<div
		:class="
			fullscreen
				? 'fixed inset-0 z-[1000] bg-white'
				: 'relative h-10 w-full max-w-[296px] ml-4'
		"
	>
		<div
			id="ts-searchbar"
			:class="
				fullscreen
					? 'relative w-full h-full flex flex-col'
					: [
							'absolute top-0 left-0 right-0 mx-auto w-full overflow-hidden rounded-lg transition-all duration-100 ease-in-out',
							{
								'min-w-[296px] h-10': !expandSearch,
								'min-w-[560px] xl:min-w-[870px] shadow-xl': expandSearch,
								'h-[224px]': expandSearch && searchQuery == '',
								'h-96': expandSearch && searchQuery.length > 0
							}
					  ]
			"
		>
			<div
				:class="
					fullscreen
						? 'bg-white w-full h-full flex flex-col'
						: 'bg-white w-full absolute inset-0 mx-auto flex flex-col shadow-xl z-50 rounded-b-lg h-full'
				"
				@click.stop
			>
				<div
					class="relative flex items-center px-2 h-14 min-h-14 lg:h-10 lg:min-h-10"
				>
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 25"
						fill="rgba(129, 136, 152, 1)"
						xmlns="http://www.w3.org/2000/svg"
						class="text-slate-400 relative z-10 shrink-0"
					>
						<path
							d="M10.0799 1.93994C5.56867 1.93994 1.91992 5.58869 1.91992 10.0999C1.91992 14.6112 5.56867 18.2599 10.0799 18.2599C11.6905 18.2599 13.1812 17.7874 14.4449 16.9849L20.3399 22.8799L22.3799 20.8399L16.5599 15.0349C17.6062 13.6624 18.2399 11.9618 18.2399 10.0999C18.2399 5.58869 14.5912 1.93994 10.0799 1.93994ZM10.0799 3.85994C13.5355 3.85994 16.3199 6.64432 16.3199 10.0999C16.3199 13.5556 13.5355 16.3399 10.0799 16.3399C6.6243 16.3399 3.83992 13.5556 3.83992 10.0999C3.83992 6.64432 6.6243 3.85994 10.0799 3.85994Z"
							fill="currentColor"
						/>
					</svg>
					<input
						ref="searchInput"
						type="text"
						v-model="searchQuery"
						placeholder="Search for news, tickers or companies"
						@input="onSearchInput"
						@click="handleClick"
						class="pl-8 py-2 placeholder:text-sm text-base bg-transparent border-b border-slate-100 text-slate-900 placeholder-slate-400 h-14 lg:h-10 absolute top-0 left-0 right-0 rounded-t-lg focus:outline-none"
					/>

					<div class="flex ml-auto gap-2 z-10">
						<template v-if="expandSearch">
							<button
								v-if="searchQuery.length > 0"
								@click="searchQuery = ''"
								class="text-slate-400 hover:text-slate-600 font-semibold text-sm transition-colors relative ml-auto px-2 py-1 rounded-md hover:bg-slate-100"
							>
								Clear
							</button>
							<button
								@click="handleClose"
								class="text-slate-400 hover:text-slate-600 transition-colors relative ml-auto p-1"
							>
								<svg
									width="20"
									height="20"
									viewBox="0 0 24 24"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M18 6L6 18M6 6L18 18"
										stroke="currentColor"
										stroke-width="2"
										stroke-linecap="round"
										stroke-linejoin="round"
									/>
								</svg>
							</button>
						</template>
					</div>
				</div>

				<!-- Trending Tickers — shows when search is expanded but query is empty -->
				<template v-if="expandSearch && searchQuery === ''">
					<div class="px-4 py-4">
						<h3 class="text-base font-semibold text-slate-900 mb-3">
							Trending Tickers
						</h3>
						<div class="flex flex-wrap gap-2">
							<template
								v-if="trendingStocks.length === 0 && stocksStore.top_gainers_loading"
							>
								<div
									v-for="i in 6"
									:key="i"
									class="h-10 w-40 animate-pulse rounded-full bg-slate-100"
								></div>
							</template>
							<template v-if="trendingStocks.length > 0">
								<NuxtLink
									v-for="stock in trendingStocks"
									:key="stock.symbol"
									:to="`/quote/${stock.symbol}/`"
									@click="$emit('close')"
									class="flex items-center gap-2 px-2 py-1 rounded-full bg-slate-50 hover:bg-slate-100 transition-colors"
								>
									<div
										v-if="imageErrors.has(stock.symbol)"
										class="w-6 h-6 rounded-full bg-black flex items-center justify-center shrink-0"
									>
										<span class="text-white text-xs font-semibold">
											{{ stock.symbol.charAt(0) }}
										</span>
									</div>
									<img
										v-else
										:src="`https://stt-ts-resources.s3.us-east-1.amazonaws.com/stock-symbols/${stock.symbol}`"
										:alt="`Logo for ${stock.symbol}`"
										class="w-6 h-6 rounded-full object-cover shrink-0"
										@error="imageErrors.add(stock.symbol)"
									/>
									<span class="text-slate-900 font-semibold text-sm">
										{{ stock.symbol }}
									</span>
									<span class="text-slate-400 text-sm">
										{{ stock.price?.toFixed(2) }}
									</span>
									<span
										class="text-sm font-medium"
										:class="{
											'text-red-400': stock.percentChange < 0,
											'text-green-500': stock.percentChange > 0,
											'text-slate-400': stock.percentChange === 0
										}"
									>
										{{ stock.percentChange >= 0 ? "+" : ""
										}}{{ stock.percentChange?.toFixed(2) }}%
									</span>
								</NuxtLink>
							</template>
						</div>
					</div>
				</template>

				<!-- Results -->
				<template v-if="searchQuery.length > 0">
					<div
						ref="resultsContainer"
						class="flex-1 overflow-y-auto pt-3 relative"
						:class="[
							fullscreen ? '' : 'max-h-[340px]',
							{ 'pb-20': hasSearched && hasResults }
						]"
					>
						<!-- Symbols -->
						<div
							v-if="results.stocks && results.stocks.length > 0"
							class="mb-4"
						>
							<div class="px-4 mb-2">
								<h3
									class="text-sm font-semibold text-slate-500 uppercase tracking-wide"
								>
									Symbols
								</h3>
							</div>
							<NuxtLink
								v-for="(stock, index) in results.stocks.slice(0, 3)"
								:key="stock.symbol"
								:to="`/quote/${stock.symbol}/`"
								@click="$emit('close')"
								:data-result-index="index"
								:ref="(el) => setResultRef(el, index)"
								class="flex items-center hover:bg-slate-50 w-full focus:outline-none transition-colors"
								:class="{ 'bg-slate-50': focusedIndex === index }"
							>
								<div
									class="py-2 flex items-center w-full px-4"
									:class="{
										'border-b border-slate-100':
											index !== results.stocks.slice(0, 3).length - 1
									}"
								>
									<div
										v-if="imageErrors.has(stock.symbol)"
										class="w-8 h-8 rounded-md bg-black flex items-center justify-center mr-3 shrink-0"
									>
										<span class="text-white text-sm font-semibold">
											{{ stock.symbol.charAt(0) }}
										</span>
									</div>
									<img
										v-else
										:src="`https://stt-ts-resources.s3.us-east-1.amazonaws.com/stock-symbols/${stock.symbol}`"
										:alt="`Logo for ${stock.symbol}`"
										class="w-8 h-8 rounded-md object-cover mr-3 shrink-0"
										@error="imageErrors.add(stock.symbol)"
									/>
									<div class="flex items-center gap-2 flex-1 min-w-0">
										<span class="text-sm font-semibold text-slate-900 shrink-0">
											{{ stock.symbol }}
										</span>
										<span class="text-sm text-slate-400 truncate">
											{{ stock.longName }}
										</span>
									</div>
									<span class="text-sm text-slate-400 shrink-0">
										{{ stock.exchange }}
									</span>
								</div>
							</NuxtLink>
						</div>

						<!-- Progress bar while loading -->
						<div
							v-if="isLoading"
							class="px-4 mb-3"
						>
							<div class="w-full bg-slate-100 rounded-full h-0.5 overflow-hidden">
								<div
									class="h-full bg-blue-500 rounded-full progress-bar"
									:style="{ width: progressWidth + '%' }"
								></div>
							</div>
						</div>

						<!-- News -->
						<div
							v-if="results.news && results.news.length > 0"
							class="mb-4"
						>
							<div class="px-4 mb-2">
								<h3
									class="text-sm font-semibold text-slate-500 uppercase tracking-wide"
								>
									News
								</h3>
							</div>
							<NuxtLink
								v-for="(newsItem, index) in results.news"
								:key="newsItem.id"
								:to="`/news/${newsItem.slug}/`"
								@click="$emit('close')"
								:data-result-index="results.stocks.slice(0, 3).length + index"
								:ref="
									(el) => setResultRef(el, results.stocks.slice(0, 3).length + index)
								"
								class="block px-4 hover:bg-slate-50 focus:outline-none transition-colors"
								:class="{
									'bg-slate-50':
										focusedIndex === results.stocks.slice(0, 3).length + index
								}"
							>
								<div
									class="py-3 border-b border-slate-100"
									:class="{ 'border-b-0': index === results.news.length - 1 }"
								>
									<p
										class="text-sm font-medium leading-snug text-slate-900"
										v-html="newsItem.title.rendered"
									></p>
									<span class="text-xs text-slate-400">
										{{ formatDate(newsItem.date) }}
									</span>
								</div>
							</NuxtLink>
						</div>

						<!-- Related Research (posts + pages) -->
						<div
							v-if="relatedResearch.length > 0"
							class="mb-4"
						>
							<div class="px-4 mb-2">
								<h3
									class="text-sm font-semibold text-slate-500 uppercase tracking-wide"
								>
									Related Research
								</h3>
							</div>
							<NuxtLink
								v-for="(item, index) in relatedResearch"
								:key="item.id"
								:to="item.url"
								@click="$emit('close')"
								:data-result-index="
									results.stocks.slice(0, 3).length + results.news.length + index
								"
								:ref="
									(el) =>
										setResultRef(
											el,
											results.stocks.slice(0, 3).length + results.news.length + index
										)
								"
								class="block px-4 hover:bg-slate-50 focus:outline-none transition-colors"
								:class="{
									'bg-slate-50':
										focusedIndex ===
										results.stocks.slice(0, 3).length + results.news.length + index
								}"
							>
								<div
									class="py-3 border-b border-slate-100"
									:class="{ 'border-b-0': index === relatedResearch.length - 1 }"
								>
									<p
										class="text-sm font-medium leading-snug text-slate-900 mb-0.5"
										v-html="item.title.rendered"
									></p>
									<span class="text-xs text-slate-400">{{ formatDate(item.date) }}</span>
								</div>
							</NuxtLink>
						</div>

						<!-- No results -->
						<template v-if="hasSearched && !hasResults && !isLoading">
							<div class="flex-1 flex flex-col">
								<div class="px-4 py-10 text-center flex flex-col items-center gap-3">
									<p class="text-slate-400 text-base">
										No results for
										<span class="text-slate-900 font-semibold">"{{ searchQuery }}"</span>
									</p>
									<p class="text-slate-400 text-sm">
										Try a valid ticker symbol or a specific company name
									</p>
								</div>
							</div>
						</template>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import Fuse from "fuse.js";
import { useStocksStore } from "~/stores/useStocksStore";
import { useDeferredSymbolsInit } from "~/composables/useDeferredSymbolsInit";

interface Stock {
	symbol: string;
	longName: string;
	exchange: string;
	SYMBOL?: string;
	NAME?: string;
	[key: string]: any;
}

interface NewsItem {
	id: number;
	title: { rendered: string };
	slug: string;
	date: string;
}

interface Post {
	id: number;
	title: { rendered: string };
	slug: string;
	date: string;
	post_type?: string;
	type?: string;
}

interface Page {
	id: number;
	title: { rendered: string };
	slug: string;
	date: string;
	post_type?: string;
	type?: string;
}

interface SearchResults {
	stocks: Stock[];
	news: NewsItem[];
	posts: Post[];
	pages: Page[];
}

const props = withDefaults(
	defineProps<{
		fullscreen?: boolean;
	}>(),
	{
		fullscreen: false
	}
);

const emit = defineEmits<{
	close: [];
	open: [];
}>();

const searchQuery = ref("");
const searchInput = ref<HTMLInputElement | null>(null);
const resultsContainer = ref<HTMLDivElement | null>(null);
const results = ref<SearchResults>({
	stocks: [],
	news: [],
	posts: [],
	pages: []
});
const isLoading = ref(false);
const hasSearched = ref(false);
const progressWidth = ref(0);
const expandSearch = ref(false);
const focusedIndex = ref<number>(-1);
const resultRefs = ref<Map<number, any>>(new Map());
const imageErrors = ref<Set<string>>(new Set());
const fuse = ref<Fuse<Stock> | null>(null);
const trendingStocks = ref<
	{
		symbol: string;
		name: string;
		price: number;
		change: number;
		percentChange: number;
	}[]
>([]);

const stocksStore = useStocksStore();
const { eagerInit: eagerInitSymbols } = useDeferredSymbolsInit();

let searchTimeout: ReturnType<typeof setTimeout> | null = null;
let keydownListener: ((event: Event) => void) | null = null;

const route = useRoute();

const hasResults = computed(
	() =>
		results.value.stocks.length > 0 ||
		results.value.news.length > 0 ||
		results.value.posts.length > 0 ||
		results.value.pages.length > 0
);

const relatedResearch = computed(() => {
	const combined = [...results.value.posts, ...results.value.pages];

	const blogPosts = combined
		.filter((item) => {
			const postType = item.post_type || item.type;
			return postType === "post" && Boolean(item.slug);
		})
		.map((item) => ({
			...item,
			url: `/blog/${item.slug}/`
		}));

	const uniqueById = blogPosts.filter(
		(item, index, arr) =>
			arr.findIndex((candidate) => candidate.id === item.id) === index
	);
	return uniqueById.sort(
		(a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
	);
});

const totalResults = computed(() => {
	return (
		Math.min(results.value.stocks.length, 3) +
		results.value.news.length +
		relatedResearch.value.length
	);
});

// Build Fuse index when symbols load
watch(
	() => stocksStore.symbols_list,
	(newData) => {
		if (import.meta.client && newData && newData.length > 0) {
			const searchData = newData.map((item: any) => ({
				symbol: item.SYMBOL || item.symbol,
				longName: item.NAME || item.name || item.longName,
				exchange: item.exchange || "",
				...item
			}));
			fuse.value = new Fuse(searchData, {
				keys: ["symbol", "longName", "NAME", "SYMBOL"],
				threshold: 0.3,
				includeScore: false,
				findAllMatches: true,
				isCaseSensitive: false
			});
		}
	},
	{ deep: true, immediate: true }
);

// Populate trending tickers from top gainers (fetched lazily when search opens)
watch(
	() => stocksStore.top_gainers_stocks,
	(newData) => {
		if (import.meta.client && newData && newData.length > 0) {
			const shuffled = [...newData].sort(() => Math.random() - 0.5);
			trendingStocks.value = shuffled.slice(0, 8).map((stock: any) => ({
				symbol: stock.SYMBOL,
				name: stock.NAME,
				price: stock.BID ?? stock.LAST ?? 0,
				change: stock.NET_CHG ?? 0,
				percentChange: stock.PC_CHG ?? 0
			}));
		}
	},
	{ immediate: true }
);

const formatDate = (dateString: string): string => {
	const date = new Date(dateString);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
	const diffDays = Math.floor(diffHours / 24);

	if (diffHours < 1) {
		const diffMin = Math.floor(diffMs / (1000 * 60));
		return `${diffMin}m ago`;
	} else if (diffHours < 24) {
		return `${diffHours}h ago`;
	} else if (diffDays < 7) {
		return `${diffDays}d ago`;
	} else {
		return date.toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric"
		});
	}
};

const setResultRef = (el: any, index: number) => {
	if (el) resultRefs.value.set(index, el);
	else resultRefs.value.delete(index);
};

const navigateToFocusedResult = () => {
	const focused = resultRefs.value.get(focusedIndex.value);
	if (focused && focused.$el) focused.$el.click();
};

const searchStocks = (query: string): Stock[] => {
	if (!fuse.value || !query) return [];
	try {
		return fuse.value
			.search(query)
			.map(({ item }) => ({
				...item,
				symbol: item.SYMBOL || item.symbol,
				longName: item.NAME || item.longName
			}))
			.slice(0, 5);
	} catch {
		return [];
	}
};

const performSearch = async () => {
	if (!searchQuery.value || searchQuery.value.trim().length < 1) {
		results.value = { stocks: [], news: [], posts: [], pages: [] };
		hasSearched.value = false;
		focusedIndex.value = -1;
		return;
	}

	hasSearched.value = true;
	const query = searchQuery.value.trim();
	const stockResults = searchStocks(query);
	results.value.stocks = stockResults;

	const queryWords = query.toLowerCase().split(/\s+/);
	const isArticleQuery =
		queryWords.length > 1 &&
		stockResults.length > 0 &&
		!queryWords.some((w) =>
			stockResults.some((s) => s.symbol.toLowerCase() === w)
		);

	if (isArticleQuery) results.value.stocks = [];

	if (stockResults.length > 0 && !isArticleQuery) {
		await nextTick();
		focusedIndex.value = 0;
	}

	const runApiSearch = async (q: string, articleMode: boolean) => {
		progressWidth.value = 0;
		await nextTick();
		const interval = setInterval(() => {
			if (progressWidth.value < 90)
				progressWidth.value = Math.min(progressWidth.value + 10, 90);
		}, 200);

		try {
			const response = await $fetch<Omit<SearchResults, "stocks">>("/api/search", {
				params: { q, ...(articleMode ? { article: "true" } : {}) }
			});
			clearInterval(interval);
			progressWidth.value = 100;
			results.value = {
				...results.value,
				news: response.news || [],
				posts: response.posts || [],
				pages: response.pages || []
			};
			if (totalResults.value > 0) {
				await nextTick();
				focusedIndex.value = 0;
			}
		} catch {
			clearInterval(interval);
			progressWidth.value = 100;
			results.value = { ...results.value, news: [], posts: [], pages: [] };
		} finally {
			setTimeout(() => {
				isLoading.value = false;
				progressWidth.value = 0;
			}, 200);
		}
	};

	if (stockResults.length === 0 || isArticleQuery) {
		await runApiSearch(query, true);
	} else {
		const first = stockResults[0] as Stock;
		await runApiSearch(`${first.symbol} - ${first.longName}`, false);
	}
};

const onSearchInput = () => {
	eagerInitSymbols();
	if (searchTimeout) clearTimeout(searchTimeout);

	results.value.news = [];
	results.value.posts = [];
	results.value.pages = [];
	focusedIndex.value = -1;
	progressWidth.value = 0;

	if (searchQuery.value.trim().length >= 1) isLoading.value = true;

	searchTimeout = setTimeout(performSearch, 500);
};

const handleInputKeyDown = (event: KeyboardEvent) => {
	if (event.key === "Escape") {
		if (searchTimeout) {
			clearTimeout(searchTimeout);
			searchTimeout = null;
		}
		removeKeydownListener();
		expandSearch.value = false;
		isLoading.value = false;
		focusedIndex.value = -1;
		resultRefs.value.clear();
		searchInput.value?.blur();
		emit("close");
		return;
	}
	if ((event.ctrlKey || event.metaKey) && event.key === "q") {
		event.preventDefault();
		clearSearch();
		return;
	}
	if (event.key === "ArrowDown") {
		event.preventDefault();
		event.stopPropagation();
		if (focusedIndex.value < totalResults.value - 1) focusedIndex.value++;
		return;
	}
	if (event.key === "ArrowUp") {
		event.preventDefault();
		event.stopPropagation();
		if (focusedIndex.value >= 0) focusedIndex.value--;
		return;
	}
	if (event.key === "Enter") {
		event.preventDefault();
		if (focusedIndex.value === -1 && totalResults.value > 0)
			focusedIndex.value = 0;
		if (focusedIndex.value >= 0) navigateToFocusedResult();
	}
};

const handleClick = () => {
	eagerInitSymbols();
	expandSearch.value = true;
	emit("open");
};

const removeKeydownListener = () => {
	if (keydownListener) {
		const container = document.querySelector("#ts-searchbar");
		if (container) container.removeEventListener("keydown", keydownListener);
		keydownListener = null;
	}
};

const clearSearch = () => {
	if (searchTimeout) {
		clearTimeout(searchTimeout);
		searchTimeout = null;
	}
	removeKeydownListener();
	searchQuery.value = "";
	results.value = { stocks: [], news: [], posts: [], pages: [] };
	hasSearched.value = false;
	isLoading.value = false;
	focusedIndex.value = -1;
	resultRefs.value.clear();
	searchInput.value?.focus();
	expandSearch.value = false;
};

const handleClose = () => {
	if (searchTimeout) {
		clearTimeout(searchTimeout);
		searchTimeout = null;
	}
	removeKeydownListener();
	expandSearch.value = false;
	searchInput.value?.blur();
	emit("close");
};

const handleOverlayClick = () => {
	if (searchTimeout) {
		clearTimeout(searchTimeout);
		searchTimeout = null;
	}
	removeKeydownListener();
	expandSearch.value = false;
	searchInput.value?.blur();
	emit("close");
};

watch(expandSearch, (newValue) => {
	if (!import.meta.client) return;
	if (newValue) {
		removeKeydownListener();
		const container = document.querySelector("#ts-searchbar");
		if (container) {
			keydownListener = (event: Event) =>
				handleInputKeyDown(event as KeyboardEvent);
			container.addEventListener("keydown", keydownListener);
		}
		// Lazy-fetch trending tickers on first open
		if (stocksStore.top_gainers_stocks.length === 0) {
			stocksStore.fetchTopGainerStocks(10);
		}
	} else {
		removeKeydownListener();
	}
});

watch(
	() => route.fullPath,
	(newValue, oldValue) => {
		if (newValue !== oldValue) {
			clearSearch();
			searchInput.value?.blur();
		}
	},
	{ immediate: true }
);

onMounted(() => {
	if (!props.fullscreen) return;
	expandSearch.value = true;
	eagerInitSymbols();
	if (import.meta.client) {
		document.body.style.overflow = "hidden";
		nextTick(() => searchInput.value?.focus());
	}
});

onBeforeUnmount(() => {
	if (props.fullscreen && import.meta.client) {
		document.body.style.overflow = "";
	}
});
</script>

<style scoped>
.progress-bar {
	transition: width 0.3s ease-out;
}

.overflow-y-auto {
	scrollbar-color: transparent transparent;
	scrollbar-width: thin;
}

.overflow-y-auto::-webkit-scrollbar {
	width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
	background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
	background: transparent;
	border-radius: 4px;
}
</style>
